import { PROFITABILITY_MARGINS } from "@/utils/profitabilityMargins";

// Series de los 3 gráficos del tab Dashboard, en el contrato de
// `KpiComposedChart` (`{ name, type, unit, data, stack?, colorToken?,
// dashed? }`). Puro: recibe `dashboard.series` normalizado por el store y
// devuelve las series de cada card, así el armado se testea sin montar la
// vista. Los % llegan como fracción y acá pasan a 0-100.
//
// Observaciones de Steve (2026-10-06): Ventas y Márgenes van en UN gráfico
// como el primero de Rentabilidad (barras de venta + una línea por escalón),
// sin la línea punteada del año anterior; el gross burn suma una línea
// punteada con las ventas del mes; EBITDA es una sola serie de barras con el
// color del escalón, sin signo ni línea de margen.

// Color por cuenta canónica del gross burn: los costos en la familia slate
// del tab Costos (misma que Rentabilidad), los gastos en la familia ámbar del
// tab Gastos. Una cuenta no canónica cae a "Otros".
const BURN_COLOR_TOKENS = Object.freeze({
	costos_productos: "--report-chart-cost-1",
	costos_directos: "--report-chart-cost-1",
	costos_variables: "--report-chart-cost-2",
	costos_fijos: "--report-chart-cost-3",
	gastos_administracion: "--report-chart-burn-gastos-1",
	gastos_comerciales: "--report-chart-burn-gastos-2",
	"gastos_i+d": "--report-chart-burn-gastos-3",
});
const OTHER_TOKEN = "--report-chart-other";
const BURN_STACK = "burn";
// Nombres cortos para la leyenda del gross burn (Steve, 2026-10-06: «sin las
// palabras comunes»): por clave canónica; una cuenta no canónica pierde el
// prefijo «Costos de / Costos / Gastos de / Gastos» si lo tiene.
const BURN_SHORT_LABELS = Object.freeze({
	costos_productos: "Productos",
	costos_directos: "Directos",
	costos_variables: "Variables",
	costos_fijos: "Fijos",
	gastos_administracion: "Administración",
	gastos_comerciales: "Comerciales",
	"gastos_i+d": "I+D",
});
const BURN_PREFIX = /^(costos?|gastos?)(\s+de)?\s+/i;

export function shortBurnLabel(key, label) {
	if (BURN_SHORT_LABELS[key]) return BURN_SHORT_LABELS[key];
	const short = String(label || "").replace(BURN_PREFIX, "").trim();
	return short || label;
}

const MARGIN_TOKENS = Object.fromEntries(
	PROFITABILITY_MARGINS.map((margin) => [margin.rowKey, margin.colorToken]),
);
const EBITDA_TOKEN = MARGIN_TOKENS["margen_EBITDA_%"];
const SALES_NAME = "Ventas";
const SALES_TOKEN = "--report-chart-sales";
// La línea de ventas sobre el gross burn: punteada y en el gris que antes
// llevaba el peer del año anterior, para no competir con las barras.
const SALES_LINE_TOKEN = "--report-chart-sales-prev";

// Fracción → 0-100 con dos decimales: quita el ruido de coma flotante
// (0.07 × 100 = 7.000000000000001) sin tocar lo que el eje redondea.
const toPercent = (values) => values.map(
	(value) => (value == null ? null : Math.round(value * 10000) / 100),
);

// Ventas & Márgenes: barras de venta (eje izquierdo, pesos) y una línea por
// escalón de margen (eje derecho, %) con la identidad de Rentabilidad.
export function salesAndMarginsSeries(series) {
	return [
		{
			name: SALES_NAME, type: "bar", unit: "money",
			data: series.sales.current, colorToken: SALES_TOKEN,
		},
		...series.margins.map((margin) => ({
			name: margin.label, type: "line", unit: "percent",
			data: toPercent(margin.data), colorToken: MARGIN_TOKENS[margin.key] || OTHER_TOKEN,
		})),
	];
}

// Gross burn: un segmento apilado por cuenta de costo o gasto más la línea
// punteada de las ventas del mes, en el mismo eje de pesos.
export function burnSeries(series) {
	return [
		...series.burn.map((account) => ({
			name: shortBurnLabel(account.key, account.label), type: "bar", unit: "money", stack: BURN_STACK,
			data: account.data, colorToken: BURN_COLOR_TOKENS[account.key] || OTHER_TOKEN,
		})),
		{
			name: SALES_NAME, type: "line", unit: "money",
			data: series.sales.current, dashed: true, colorToken: SALES_LINE_TOKEN,
		},
	];
}

// EBITDA: barras con el color fijo del escalón, también cuando es pérdida.
export function ebitdaSeries(series) {
	return [
		{ name: "EBITDA", type: "bar", unit: "money", data: series.ebitda.amount, colorToken: EBITDA_TOKEN },
	];
}

// True si la serie tiene al menos un dato distinto de null.
export function hasAnyData(seriesList) {
	return seriesList.some((serie) => (serie.data || []).some((value) => value != null));
}
